// Beta-reader rounds sent as web links (design §2.7, PROTOCOL.md §7). The Swift's `ReaderRound.Format` is a closed enum
// (pdf, epub, docx) and `readerRounds()` decodes each round with `try?`, so a round holding a "web" reader would vanish
// from the Mac's list. Web rounds therefore live under their own key, `settings.webReaderRounds` (JSON per round, keyed
// by round id, sorted keys and ISO 8601 dates like `readerRounds`), which the Swift leaves alone. Each reader has their
// own link: its review id and review key (base64url) are kept here, in the project document, which only the writer's
// devices can open, so any of them can show the link again or place its notes.
import * as A from '@automerge/automerge';
import { readProject } from '../model/documents.ts';
import { changeTime, str, Writer } from '../edit/typed.ts';
import { SwiftReader } from '../automerge/swiftDoc.ts';
import { fromISO8601, object, optionalString, sortedJSON, string, toISO8601 } from './json.ts';

export const WEB_ROUNDS_KEY = 'webReaderRounds';

export interface WebReader {
  id: string;
  name: string;
  format: 'web';
  /** The link's review on the relay. */
  reviewID: string;
  /** The link's review key, base64url (the part after `#`). */
  key: string;
  /** A hard expiry the relay enforces (410 after it), if any. */
  expiresAt?: Date;
  /** The link was withdrawn: the relay deleted its copy and notes. */
  revokedAt?: Date;
}

export interface WebReaderRound {
  id: string;
  name: string;
  /** The checkpoint every link shows (their comments anchor to it). */
  checkpointID: string;
  createdAt: Date;
  /** When notes are wanted by; shown on every copy. */
  returnBy?: Date;
  /** Said on each copy under the reader's name. */
  message: string;
  readers: WebReader[];
}

export const encodeWebRound = (round: WebReaderRound) => sortedJSON(round, toISO8601);

export function decodeWebRound(json: string): WebReaderRound {
  const o = object(JSON.parse(json), 'The round');
  const readers = Array.isArray(o.readers) ? o.readers : [];
  const date = (v: unknown) => (v === undefined || v === null ? undefined : fromISO8601(v));
  return {
    id: string(o, 'id'),
    name: string(o, 'name'),
    checkpointID: string(o, 'checkpointID'),
    createdAt: fromISO8601(o.createdAt),
    ...(date(o.returnBy) ? { returnBy: date(o.returnBy) } : {}),
    message: optionalString(o, 'message') ?? '',
    readers: readers.flatMap((r) => {
      try {
        const x = object(r, 'A reader');
        const expiresAt = date(x.expiresAt), revokedAt = date(x.revokedAt);
        return [{
          id: string(x, 'id'), name: string(x, 'name'), format: 'web' as const, reviewID: string(x, 'reviewID'), key: string(x, 'key'),
          ...(expiresAt ? { expiresAt } : {}), ...(revokedAt ? { revokedAt } : {}),
        }];
      } catch {
        return []; // one unreadable reader doesn't lose the round
      }
    }),
  };
}

/** The project's web rounds, newest first; unreadable ones skipped. */
export function webRounds(project: A.Doc<unknown>): WebReaderRound[] {
  const raw = readProject(project).settings[WEB_ROUNDS_KEY];
  if (!raw || typeof raw !== 'object') return [];
  const out: WebReaderRound[] = [];
  for (const v of Object.values(raw as Record<string, unknown>)) {
    if (typeof v !== 'string') continue;
    try {
      out.push(decodeWebRound(v));
    } catch {
      // unreadable: left as it is
    }
  }
  return out.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

/** Every link of every round: review id → its round and reader. */
export function webLinks(project: A.Doc<unknown>): Map<string, { round: WebReaderRound; reader: WebReader }> {
  const out = new Map<string, { round: WebReaderRound; reader: WebReader }>();
  for (const round of webRounds(project)) for (const reader of round.readers) out.set(reader.reviewID, { round, reader });
  return out;
}

/** Stores a round (`putJSON`), committed as the Swift's `Beta round “…”`. Returns the document names changed. */
export function putWebRound(docs: Map<string, A.Doc<unknown>>, round: WebReaderRound, now = new Date()): string[] {
  const doc = docs.get('project');
  if (!doc) throw new Error('No project document');
  const r = new SwiftReader(doc);
  const before = A.getHeads(doc);
  const next = A.change(doc as A.Doc<Record<string, any>>, { message: `Beta round “${round.name}”`, time: changeTime(now) }, (d) =>
    new Writer(d, r).putIfChanged(['settings', WEB_ROUNDS_KEY], round.id, str(encodeWebRound(round))),
  );
  const after = A.getHeads(next);
  if (after.length === before.length && after.every((h, i) => h === before[i])) return [];
  docs.set('project', next as A.Doc<unknown>);
  return ['project'];
}
